/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { extractApiErrorMessage } from '../../lib/apiError';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import { AppWindow, ClipboardList, Cog, FileText, LayoutGrid, Network, Plus, Trash2, Users, X } from 'lucide-react';
import { AskAgentButton } from '../../components/Common/AskAgentButton';
import { DomainWithDetails, DomainStats, DomainMember, DomainUpdate, UseCaseSummary, Forge } from '../../types';
import { domainService } from '../../services/domainService';
import { Button } from '../../components/Common/button';
import { DomainForm } from '../../components/Domains/DomainForm';
import { DomainDocumentsGraph } from '../../components/Domains/DomainDocumentsGraph';
import { PageLayout } from '../../components/Layout/PageLayout';
import { NotFoundState } from '../../components/Common/NotFoundState';
import { ConfirmationModal } from '../../components/Common/ConfirmationModal';
import { useCaseService } from '../../services/useCaseService';
import cadrageService from '../../services/cadrageService';
import questionnaireService from '../../services/questionnaireService';
import UseCaseListTable from '../../components/UseCases/UseCaseListTable';
import { OneForgeModule, ApplicationListModule, ForgeSelector } from '../../components/Forge';
import forgeService from '../../services/forgeService';
import { useForgeApiEnabled } from '../../stores/forgeApiStore';
import DocumentManagerSection from '../../components/Documents/DocumentManagerSection';
import { readStoredDocument } from '../../services/documentReaderService';
import { useDomainContextGraph } from '../../hooks/useDomainContextGraph';
import notify from '../../lib/notify';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DomainQuestionnairesList: React.FC<{ domainId: string; cadrages?: any[]; onCountChange?: (n: number) => void }> = ({ domainId, cadrages, onCountChange }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [creating, setCreating] = useState<boolean>(false);
  const [newTemplateId, setNewTemplateId] = useState<string>("");
  const [newCadrageId, setNewCadrageId] = useState<string>("");
  const [templates, setTemplates] = useState<any[]>([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await questionnaireService.listDomainResponses(domainId, { skip: page * pageSize, limit: pageSize });
        if (mounted) setItems(data || []);
      } catch (e: any) {
        if (mounted) setError(e?.message || t('domainDetail.questionnaires.loadError'));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [domainId, page, pageSize]);

  useEffect(() => {
    if (onCountChange) onCountChange(items.length);
  }, [items, onCountChange]);

  if (loading) return <p className="text-muted-foreground">{t('domainDetail.questionnaires.loading')}</p>;
  if (error) return <p className="text-destructive">{error}</p>;
  if (!items.length) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-3">{t('domainDetail.questionnaires.noResponses')}</p>
        {!creating ? (
          <Button size="sm" variant="outline" onClick={async () => {
            setCreating(true);
            const tpls = await questionnaireService.list();
            setTemplates(tpls || []);
          }}>{t('domainDetail.questionnaires.newQuestionnaire')}</Button>
        ) : (
          <div className="max-w-xl mx-auto p-4 border border-border rounded-lg text-left">
            <div className="grid gap-3">
              <select className="border border-border rounded px-3 py-2 text-sm" value={newTemplateId} onChange={(e) => setNewTemplateId(e.target.value)}>
                <option value="">{t('domainDetail.questionnaires.selectTemplate')}</option>
                {templates.map((template: any) => (
                  <option key={template.id} value={template.id}>{template.name}</option>
                ))}
              </select>
              <select className="border border-border rounded px-3 py-2 text-sm" value={newCadrageId} onChange={(e) => setNewCadrageId(e.target.value)}>
                <option value="">{t('domainDetail.questionnaires.selectCadrage')}</option>
                {(cadrages || []).map((c: any) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <Button size="sm" onClick={async () => {
                  if (!newTemplateId || !newCadrageId) return;
                  await questionnaireService.createForCadrage(newCadrageId, { template_id: newTemplateId });
                  setCreating(false);
                  setNewTemplateId("");
                  setNewCadrageId("");
                  setPage(0);
                }}>{t('global.create')}</Button>
                <Button size="sm" variant="outline" onClick={() => { setCreating(false); setNewTemplateId(""); setNewCadrageId(""); }}>{t('global.cancel')}</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((it: any) => (
        <div key={it.id} className="p-4 border border-border rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-foreground">{it.template?.name || t('domainDetail.questionnaires.response')}</p>
              {it.status && <p className="text-sm text-muted-foreground">{it.status}</p>}
            </div>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between pt-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{t('domainDetail.questionnaires.page')} {page + 1}</span>
          <select className="border border-border rounded px-2 py-1 bg-card" value={pageSize} onChange={(e) => { setPage(0); setPageSize(parseInt(e.target.value, 10)); }}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('global.previous')}</Button>
          <Button variant="outline" size="sm" disabled={items.length < pageSize} onClick={() => setPage((p) => p + 1)}>{t('global.next')}</Button>
        </div>
      </div>
    </div>
  );
};
const DomainDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { domainId } = useParams<{ domainId: string }>();
  const navigate = useNavigate();

  const [domain, setDomain] = useState<DomainWithDetails | null>(null);
  const [stats, setStats] = useState<DomainStats | null>(null);
  const [members, setMembers] = useState<DomainMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [useCases, setUseCases] = useState<UseCaseSummary[]>([]);
  const [useCasesLoading, setUseCasesLoading] = useState<boolean>(false);
  const [useCasesError, setUseCasesError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [domainNameInput, setDomainNameInput] = useState('');

  const domainGraph = useDomainContextGraph(domainId ?? undefined);

  // Forge integration state
  const [mainTab, setMainTab] = useState<'overview' | 'knowledge' | 'documents' | 'usecases' | 'forge' | 'applications'>('overview');
  const [selectedForgeId, setSelectedForgeId] = useState<string | null>(null);
  const [selectedForge, setSelectedForge] = useState<Forge | null>(null);
  const [forgeLoadError, setForgeLoadError] = useState<string | null>(null);
  const [showChangeForge, setShowChangeForge] = useState(false);
  const [pendingForgeId, setPendingForgeId] = useState<string | null>(null);
  const [pendingForge, setPendingForge] = useState<Forge | null>(null);
  const [isSavingForge, setIsSavingForge] = useState(false);
  const hasForgeClient = Boolean(domain?.organization?.forge_client_id);
  // The Forge / Applications tabs should appear whenever the domain has a
  // linked forge (something to show) OR the org has a forge_client (so the user
  // can pick one). hasForgeClient alone hid the tab on freshly-loaded domains
  // where the nested organization wasn't carrying forge_client_id yet.
  const forgeApiEnabled = useForgeApiEnabled();
  const showForgeTabs = forgeApiEnabled === true && (hasForgeClient || Boolean(domain?.forge_id));

  useEffect(() => {
    if (domainId) {
      loadDomainData(domainId);
    }
  }, [domainId]);

  // Load forge info when domain has forge_id
  useEffect(() => {
    if (domain?.forge_id && forgeApiEnabled === true) {
      const forgeId = domain.forge_id;
      setSelectedForgeId(forgeId);
      setForgeLoadError(null);
      forgeService.getForge(forgeId)
        .then(setSelectedForge)
        .catch((err: any) => {
          console.error('Error loading forge:', err);
          // From the user's POV the linked Forge is unusable — show a neutral
          // "no Forge attached" message and let them re-link via the existing
          // change-Forge action. The upstream cause (404 stale link, 502 forge
          // API down, 403 access lost) doesn't change what they can do here.
          setForgeLoadError(t('domainDetail.forge.notAttached', 'Aucune Forge attachée'));
          // Hidden super-admin notification with business context (forge id +
          // domain identifiers). The apiClient interceptor already emits a
          // generic API error; this one adds the entity scope so a super-admin
          // can investigate the broken link directly.
          const httpStatus = err?.response?.status;
          const apiDetail =
            (err?.response?.data && typeof err.response.data === 'object'
              ? err.response.data.detail
              : undefined) || err?.message || 'Unknown error';
          notify.systemError(
            `Forge load failed for domain "${domain?.name ?? domain?.id}" (forge_id=${forgeId}): ${apiDetail}`,
            {
              errorType: err?.name || 'ForgeLoadError',
              httpStatus,
              endpoint: `/forge/forges/${forgeId}`,
              httpMethod: 'GET',
              extra: {
                domain_id: String(domain?.id ?? ''),
                domain_name: String(domain?.name ?? ''),
                forge_id: String(forgeId),
              },
            },
            t('domainDetail.forge.unreachable'),
          );
        });
    }
  }, [domain?.forge_id, forgeApiEnabled]);

  useEffect(() => {
    if (!domainId) return;
    let mounted = true;
    (async () => {
      try {
        setUseCasesLoading(true);
        setUseCasesError(null);
        const [data, allCadrages] = await Promise.all([
          useCaseService.list({ domain_id: domainId }),
          cadrageService.getCadrages().catch(() => []),
        ]);
        const baseUseCases = data || [];
        const domainUseCaseIds = new Set(baseUseCases.map((u) => u.id));
        const useCasesWithLinkedCadrage = new Set(
          (allCadrages || [])
            .map((c) => c.use_case_id)
            .filter((linkedUseCaseId): linkedUseCaseId is string => Boolean(linkedUseCaseId) && domainUseCaseIds.has(linkedUseCaseId))
        );

        // Always enrich with detail payload to ensure linkage columns
        // (cadrage/app) are accurate in the domain overview table.
        const enrichedUseCases = await Promise.all(
          baseUseCases.map(async (useCase) => {
            const hasLinkedCadrage = useCasesWithLinkedCadrage.has(useCase.id);
            try {
              const details = await useCaseService.get(useCase.id);
              return {
                ...useCase,
                cadrage_id: hasLinkedCadrage ? (details.cadrage_id ?? useCase.cadrage_id ?? null) : null,
                has_linked_cadrage: hasLinkedCadrage,
                forge_application_id: details.forge_application_id,
              };
            } catch {
              return {
                ...useCase,
                cadrage_id: hasLinkedCadrage ? (useCase.cadrage_id ?? null) : null,
                has_linked_cadrage: hasLinkedCadrage,
              };
            }
          })
        );
        if (mounted) setUseCases(enrichedUseCases);
      } catch (e: any) {
        if (mounted) setUseCasesError(e?.message || t('domainDetail.useCasesError'));
      } finally {
        if (mounted) setUseCasesLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [domainId]);

  useEffect(() => {
    if (!showForgeTabs && (mainTab === 'forge' || mainTab === 'applications')) {
      setMainTab('overview');
    }
  }, [showForgeTabs, mainTab]);

  const loadDomainData = async (id: string) => {
    setIsLoading(true);
    setError(null);
    setLoadError(null);

    try {
      const [domainData, statsData, membersData] = await Promise.all([
        domainService.getDomainDetails(id),
        domainService.getDomainStats(id),
        domainService.getDomainMembers(id)
      ]);

      setDomain(domainData);
      setStats(statsData);
      setMembers(membersData);
    } catch (err) {
      console.error('Error loading domain data:', err);
      setError(t('domainDetail.loading'));
      setLoadError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateDomain = async (data: DomainUpdate) => {
    if (!domainId) return;

    try {
      setIsUpdating(true);
      setUpdateError(null);
      setSuccessMessage(null);

      const updatedDomain = await domainService.updateDomain(domainId, data);

      // Update the local domain state
      setDomain(prev => prev ? { ...prev, ...updatedDomain } : null);
      setSuccessMessage(t('domains.edit.updateSuccess'));

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('Failed to update domain', err);
      setUpdateError(
        extractApiErrorMessage(err, t('domainDetail.updateError'))
      );
    } finally {
      setIsUpdating(false);
    }
  };


  const handleDeleteDomain = () => {
    setDomainNameInput('');
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!domainId || !domain) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);

      await domainService.deleteDomain(domainId);

      // Navigate back to domains list with success message
      navigate('/domains', {
        state: { message: t('domainDetail.deleteSuccess', { name: domain.name }) }
      });
    } catch (err: any) {
      console.error('Failed to delete domain', err);
      setDeleteError(
        extractApiErrorMessage(err, t('domainDetail.deleteError'))
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDomainNameInput('');
    setDeleteError(null);
  };

  const handleSaveForge = async () => {
    if (!domainId) return;
    try {
      setIsSavingForge(true);
      await domainService.updateDomain(domainId, { forge_id: pendingForgeId });
      setDomain(prev => prev ? { ...prev, forge_id: pendingForgeId } : null);
      setSelectedForgeId(pendingForgeId);
      setSelectedForge(pendingForge);
      setForgeLoadError(null);
      setShowChangeForge(false);
      setPendingForgeId(null);
      setPendingForge(null);
    } catch (err: any) {
      console.error('Failed to update forge', err);
    } finally {
      setIsSavingForge(false);
    }
  };

  if (isLoading) {
    return (
      <PageLayout icon={<LayoutGrid className="h-6 w-6" />} title={t('global.domain')} subtitle={t('global.loading')}>
        <div className="animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-muted rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !domain) {
    return (
      <PageLayout icon={<LayoutGrid className="h-6 w-6" />} title={t('global.domain')} subtitle={error || t('domainDetail.notFound')}>
        <NotFoundState error={loadError}>
          <div className="text-center">
            <p className="text-destructive mb-4">{error || t('domainDetail.notFound')}</p>
            <Button onClick={() => navigate('/welcome')} variant="default">
              {t('domainDetail.backToDashboard')}
            </Button>
          </div>
        </NotFoundState>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      icon={<LayoutGrid className="h-6 w-6" />}
      title={domain.name}
      subtitle={domain.description || ''}
      titleSuffix={
        domainId ? (
          <AskAgentButton entityType="domain" entityId={domainId} entityName={domain.name} />
        ) : null
      }
    >
      <div className="space-y-6">
      {/* Delete Error */}
      {deleteError && (
        <div className="p-4 bg-destructive/10 border border-destructive rounded-lg text-destructive">
          <div className="flex">
            <div className="flex-shrink-0">
              <X className="h-5 w-5" />
            </div>
            <div className="ml-3">
              <p className="text-sm">{deleteError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-border">
          <nav className="flex space-x-8">
            <button
              onClick={() => setMainTab('overview')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                mainTab === 'overview'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t('domainDetail.tabs.overview', 'Vue d\'ensemble')}
            </button>
            <button
              onClick={() => setMainTab('knowledge')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                mainTab === 'knowledge'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t('domainDetail.tabs.knowledge')}
            </button>
            <button
              onClick={() => setMainTab('documents')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                mainTab === 'documents'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t('domainDetail.tabs.documents')}
            </button>
            <button
              onClick={() => setMainTab('usecases')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                mainTab === 'usecases'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {t('domainDetail.tabs.useCases', 'Cas d\'usage')}
            </button>
            {showForgeTabs && (
              <button
                onClick={() => setMainTab('forge')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  mainTab === 'forge'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                {t('domainDetail.tabs.forge', 'Forge')}
                {selectedForgeId && <span className="ml-1 text-xs text-green-500">●</span>}
              </button>
            )}
            {showForgeTabs && (
              <button
                onClick={() => setMainTab('applications')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors inline-flex items-center gap-1.5 ${
                  mainTab === 'applications'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <AppWindow className="h-4 w-4 shrink-0" aria-hidden />
                {t('domainDetail.tabs.applications', 'Applications')}
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {mainTab === 'overview' && (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium text-foreground">{t('domainDetail.generalInfo')}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t('domainDetail.generalInfoDescription')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center gap-2"
            >
              <Cog className="h-4 w-4" />
              <span>{t('global.edit')}</span>
            </Button>
            <Button
              onClick={handleDeleteDomain}
              disabled={isDeleting}
              variant="destructive"
              size="sm"
              className="flex items-center gap-2"
            >
              <Trash2 className="h-4 w-4" />
              <span>{isDeleting ? t('global.deleting') : t('global.delete')}</span>
            </Button>
          </div>
        </div>

        <dl className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">{t('global.name')}</dt>
            <dd className="mt-1 text-sm text-foreground">{domain.name}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">{t('domainDetail.organization')}</dt>
            <dd className="mt-1 text-sm text-foreground">
              {domain.organization?.name || t('global.notAvailable')}
            </dd>
          </div>
          {domain.description && (
            <div className="md:col-span-1">
              <dt className="text-sm font-medium text-muted-foreground">{t('global.description')}</dt>
              <dd className="mt-1 text-sm text-foreground">{domain.description}</dd>
            </div>
          )}
          <div>
            <dt className="text-sm font-medium text-muted-foreground">{t('domainDetail.createdDate')}</dt>
            <dd className="mt-1 text-sm text-foreground">
              {new Date(domain.created_at).toLocaleDateString(i18n.language)}
            </dd>
          </div>
        </dl>

        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 pt-2">
            <div className="bg-muted/40 p-4 rounded-lg border border-border/60 relative">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-7 w-7 text-blue-500" />
                </div>
                <div className="ml-4">
                  <p className="text-xl font-semibold text-card-foreground">
                    {stats.total_members}
                  </p>
                  <p className="text-sm text-muted-foreground">{t('domainDetail.members')}</p>
                </div>
              </div>
              <button
                aria-label={t('domainDetail.members')}
                onClick={() => setShowMembersModal(true)}
                className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
              >
                <Users className="h-4 w-4" />
              </button>
            </div>

            <div className="bg-muted/40 p-4 rounded-lg border border-border/60">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClipboardList className="h-7 w-7 text-purple-500" />
                </div>
                <div className="ml-4">
                  <p className="text-xl font-semibold text-card-foreground">
                    {stats.total_cadrages}
                  </p>
                  <p className="text-sm text-muted-foreground">{t('domainDetail.cadrages')}</p>
                  <p className="text-xs text-muted-foreground">
                    {stats.active_cadrages} {t('domainDetail.activeCadrages')}, {stats.completed_cadrages} {t('domainDetail.completedCadrages')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-muted/40 p-4 rounded-lg border border-border/60">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-7 w-7 bg-muted rounded-lg flex items-center justify-center">
                    <span className="text-foreground text-xs font-semibold">UC</span>
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-xl font-semibold text-card-foreground">
                    {stats.total_use_cases}
                  </p>
                  <p className="text-sm text-muted-foreground">{t('domainDetail.useCases')}</p>
                </div>
              </div>
            </div>

            <div className="bg-muted/40 p-4 rounded-lg border border-border/60">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-7 w-7 text-amber-500" />
                </div>
                <div className="ml-4">
                  <p className="text-xl font-semibold text-card-foreground">
                    {stats.total_documents}
                  </p>
                  <p className="text-sm text-muted-foreground">{t('domainDetail.stats.documents')}</p>
                  <p className="text-xs text-muted-foreground">{t('domainDetail.stats.documentsScope')}</p>
                </div>
              </div>
            </div>

            <div className="bg-muted/40 p-4 rounded-lg border border-border/60">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Network className="h-7 w-7 text-emerald-500" />
                </div>
                <div className="ml-4">
                  <p className="text-xl font-semibold text-card-foreground">
                    {domainGraph.conceptCount ?? (domainGraph.loading ? '…' : '–')}
                  </p>
                  <p className="text-sm text-muted-foreground">{t('domainDetail.stats.knowledge')}</p>
                  <p className="text-xs text-muted-foreground">{t('domainDetail.stats.concepts')}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Knowledge Tab */}
      {mainTab === 'knowledge' && (
        <div className="bg-card rounded-lg shadow-sm">
          <DomainDocumentsGraph
            graph={domainGraph.graph}
            loading={domainGraph.loading}
            error={domainGraph.error}
            onReload={() => void domainGraph.reload()}
          />
        </div>
      )}

      {/* Documents Tab */}
      {mainTab === 'documents' && (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 space-y-4">
        <div>
          <h2 className="text-lg font-medium text-foreground">{t('domainDetail.documents.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('domainDetail.documents.description')}
          </p>
        </div>

        <DocumentManagerSection
          context={domainId ? { type: 'domain', id: domainId } : undefined}
          onImportToAIExtraction={async (item) => {
            try {
              if (!item.documentId) {
                notify.error(t('cadrage.detail.errors.documentIdNotFound'));
                return;
              }
              const result = await readStoredDocument(item.documentId);
              if (!result.success || !result.content.trim()) {
                notify.error(result.error || t('cadrage.detail.errors.noExtractableContent'));
                return;
              }
              navigate('/ai-extraction', {
                state: {
                  initialText: result.content,
                  source: {
                    type: 'domain_document',
                    documentId: item.documentId,
                    filename: result.filename,
                    domainId,
                  },
                },
              });
            } catch (e: unknown) {
              console.error(t('cadrage.detail.errors.documentReadError'), e);
              notify.error(
                e instanceof Error ? e.message : t('cadrage.detail.errors.documentReadError')
              );
            }
          }}
        />
      </div>
      )}

      {/* Use Cases Tab */}
      {mainTab === 'usecases' && (
      <div className="bg-card rounded-lg shadow-sm border border-border p-6 space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium text-foreground">{t('domainDetail.useCasesTitle')}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t('domainDetail.useCasesDescription')}
            </p>
          </div>
          <Button
            className="flex items-center gap-2 shrink-0"
            onClick={() => navigate('/cas-usage/new', { state: { domain } })}
          >
            <Plus className="h-4 w-4" />
            {t('useCases.actions.new')}
          </Button>
        </div>

        {useCasesLoading && (
          <p className="text-sm text-muted-foreground">{t('domainDetail.loadingUseCases')}</p>
        )}
        {useCasesError && (
          <p className="text-sm text-destructive">{useCasesError}</p>
        )}
        {!useCasesLoading && !useCasesError && useCases.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t('domainDetail.noUseCases')}
          </p>
        )}
        {!useCasesLoading && !useCasesError && useCases.length > 0 && (
          <UseCaseListTable
            useCases={useCases}
            onViewDetailsClick={(id) => navigate(`/cas-usage/${id}`)}
          />
        )}
      </div>
      )}

      {/* Forge Tab */}
      {showForgeTabs && mainTab === 'forge' && (
        <div className="space-y-4">
          {/* Forge error warning banner */}
          {forgeLoadError && (
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground">
                    {forgeLoadError}
                  </p>
                  {!showChangeForge && (
                    <button
                      onClick={() => {
                        setPendingForgeId(selectedForgeId);
                        setPendingForge(selectedForge);
                        setShowChangeForge(true);
                      }}
                      className="mt-2 text-sm font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                    >
                      {t('domainDetail.forge.changeForge', 'Changer la Forge associée')}
                    </button>
                  )}
                </div>
              </div>

              {showChangeForge && (
                <div className="mt-4 pt-4 border-t border-border space-y-3">
                  <ForgeSelector
                    organizationId={domain?.organization_id ?? null}
                    selectedForgeId={pendingForgeId}
                    onSelect={(forgeId, forge) => {
                      setPendingForgeId(forgeId);
                      setPendingForge(forge);
                    }}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleSaveForge}
                      disabled={isSavingForge || pendingForgeId === selectedForgeId}
                    >
                      {isSavingForge ? t('global.saving', 'Enregistrement…') : t('global.save', 'Enregistrer')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowChangeForge(false);
                        setPendingForgeId(null);
                        setPendingForge(null);
                      }}
                    >
                      {t('global.cancel')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedForgeId && !forgeLoadError ? (
            <OneForgeModule
              forgeId={selectedForgeId}
              domainId={domainId}
              domainName={domain?.name}
              hideDomainLink={true}
              showApplicationsList={true}
              onApplicationClick={(appId) => navigate(`/applications/${appId}`)}
            />
          ) : !selectedForgeId ? (
            <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
              <div className="max-w-md mx-auto">
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {t('domainDetail.forge.noForgeLinked', 'Aucune Forge liée')}
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {t('domainDetail.forge.linkForgeDescription', 'Liez ce domaine à une Forge pour afficher ses informations et applications.')}
                </p>
                <Button variant="outline" onClick={() => setShowSettingsModal(true)}>
                  {t('domainDetail.forge.configureForge', 'Configurer la Forge')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Applications Tab */}
      {showForgeTabs && mainTab === 'applications' && (
        <div className="space-y-6">
          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <div className="mb-4">
              <h2 className="text-lg font-medium text-foreground">
                {t('domainDetail.applications.title', 'Applications Forge')}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t('domainDetail.applications.description', 'Liste des applications Forge liées aux cas d\'usage de ce domaine.')}
              </p>
            </div>
            {selectedForgeId ? (
              <ApplicationListModule
                forgeId={selectedForgeId}
                showUseCaseLink={true}
                showCadrageLink={true}
                onApplicationClick={(appId) => navigate(`/applications/${appId}`)}
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  {t('domainDetail.applications.noForge', 'Liez d\'abord ce domaine à une Forge pour voir ses applications.')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && domain && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowSettingsModal(false)}></div>
          <div className="relative bg-card rounded-lg shadow-lg border border-border w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-medium text-foreground">{t('domainDetail.settingsModalTitle')}</h3>
              <button aria-label={t('global.close')} onClick={() => setShowSettingsModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              {successMessage && (
                <div className="bg-accent/10 border border-accent rounded-lg p-4 mb-4">
                  <p className="text-foreground">{successMessage}</p>
                </div>
              )}
              {updateError && (
                <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-4 text-destructive">
                  <p>{updateError}</p>
                </div>
              )}
              <DomainForm
                domain={domain}
                onSubmit={async (data) => {
                  await handleUpdateDomain(data);
                  setShowSettingsModal(false);
                }}
                onCancel={() => {
                  setUpdateError(null);
                  setSuccessMessage(null);
                  setShowSettingsModal(false);
                }}
                loading={isUpdating}
              />
            </div>
          </div>
        </div>
      )}

      {/* Members Modal */}
      {showMembersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMembersModal(false)}></div>
          <div className="relative bg-card rounded-lg shadow-lg border border-border w-full max-w-3xl mx-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-medium text-foreground">{t('domainDetail.membersModalTitle')}</h3>
              <button aria-label={t('global.close')} onClick={() => setShowMembersModal(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('domainDetail.organizationMembersInfo')}
                </p>
              </div>

              {members.length > 0 ? (
                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                  {members.map((member) => (
                    <div key={member.user_id} className="flex items-center justify-between p-4 border border-border rounded-lg">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                          <span className="text-sm font-medium text-foreground">{member.user?.name?.charAt(0) || '?'}</span>
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-foreground">{member.user?.name || t('domainDetail.unknownUser')}</p>
                          <p className="text-sm text-muted-foreground">{member.user?.email || t('domainDetail.unknownEmail')}</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
                        {t('domainDetail.member')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">{t('domainDetail.noMembers')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={showDeleteModal}
        title={t('domainDetail.deleteTitle')}
        message={t('domainDetail.deleteMessage', { name: domain?.name || '' })}
        confirmText={t('domainDetail.deleteConfirm')}
        cancelText={t('global.cancel')}
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={isDeleting}
        requireTextMatch={{
          text: domain?.name || '',
          label: t('domainDetail.deleteLabel')
        }}
        inputValue={domainNameInput}
        onInputChange={setDomainNameInput}
      />

      </div>
    </PageLayout>
  );
};

export default DomainDetailPage;
